import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { Formik, getIn } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, Modal } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { combineStrings } from 'Shared/utils';
import { useLazyBaseApi } from 'Shared/services/Api';
import { updatePortalUserPartner } from 'Admin/authPolicies';
import { PartnerType, Typeahead, TypeaheadResult } from 'Admin/Partners';
import { UserType } from 'Admin/Users/types';
import {
  PartnerTypeaheadResponse,
  getFetchPartnersConfig,
} from 'Admin/services/endpoints/PostAttributionTypeahead';
import {
  PostChangePartnerResponseType,
  transformChangePartnerResponse,
} from 'Admin/services/endpoints/PostChangePartner';
import styles from './styles.module.css';
import { useToast } from 'Shared/components';

type MoveUserFormType = Record<
  string,
  {
    agency: {
      displayValue?: string;
      selectedId?: string;
    };
  }
>;

export const MoveUser: FC<{
  user: UserType;
  currentPartner?: PartnerType;
}> = ({ user, currentPartner }) => {
  const { user: currentUser } = useAuth0();
  const history = useHistory();
  const { toast } = useToast();

  const [{ loading: loadingPartners }, fetchPartners] = useLazyBaseApi<
    PartnerTypeaheadResponse
  >({});

  const [{ error: changePartnerError }, changePartner] = useLazyBaseApi<
    PostChangePartnerResponseType
  >({
    method: 'POST',
    url: `agent_services/portal_users/${user.id}/change_partner`,
    transformResponse: transformChangePartnerResponse,
  });

  const showMoveUserBanner =
    updatePortalUserPartner(currentUser!.effectiveGrants) &&
    user.activated &&
    currentPartner;

  const prefix = useMemo(() => new Date().getTime().toString(), []);
  const initialValues = useMemo(() => {
    return {
      // to prevent browser autofill on typeaheads
      [prefix]: {
        agency: {
          displayValue: '',
          selectedId: '',
        },
      },
    };
  }, [prefix]);

  const validations = Yup.object().shape({
    [prefix]: Yup.object().shape({
      agency: Yup.object().shape({
        selectedId: Yup.string().required('Agency is required'),
      }),
    }),
  });

  return showMoveUserBanner ? (
    <Alert
      data-testid="move-user-banner"
      message={`If this user is no longer affiliated with ${
        currentPartner!.name
      }, please move them to the correct agency.`}
      title="Move User"
      type="light-brand"
      className={styles.alert}
      action={
        <Modal
          variant="normal"
          toggle={openModal => (
            <Button
              data-testid="move-user-open-modal-button"
              link
              variant="primary"
              size="sm"
              onClick={openModal}
              className={styles.action}
            >
              Move User
            </Button>
          )}
        >
          {close => (
            <Formik<MoveUserFormType>
              initialValues={initialValues}
              onSubmit={async values => {
                try {
                  const response = await changePartner({
                    data: {
                      destination_partner_id: values[prefix].agency.selectedId,
                    },
                  });

                  toast({
                    message: 'User successfully moved',
                    persistOnNavigation: true,
                  });

                  history.replace(
                    `/admin/partners/${response.data.newPartnerId}/users/${user.id}`,
                    { refreshData: true } // To trigger user details page to refresh data
                  );
                } catch {}
              }}
              validationSchema={validations}
            >
              {({
                values,
                errors,
                touched,
                setFieldValue,
                isSubmitting,
                setValues,
                handleSubmit,
                handleBlur,
              }) => (
                <div
                  data-testid="move-user-modal"
                  className={classnames('fs-mask', styles.modalContainer)}
                >
                  <h1 className={styles.heading}>
                    Move {combineStrings([user.firstName, user.lastName])} to a
                    different agency
                  </h1>
                  <Typeahead<PartnerTypeaheadResponse>
                    data-testid="partner-typeahead"
                    name={`${prefix}.agency.displayValue`}
                    label="Search Agency"
                    debounce={200}
                    value={values[prefix].agency.displayValue}
                    setValue={value => {
                      setFieldValue(`${prefix}.agency.displayValue`, value);
                    }}
                    errorMessage={
                      getIn(touched, `${prefix}.agency.displayValue`)
                        ? getIn(errors, `${prefix}.agency.selectedId`)
                        : undefined
                    }
                    loading={loadingPartners}
                    lazyRequest={fetchPartners}
                    getLazyRequestConfig={getFetchPartnersConfig}
                    isClearable={!!values[prefix].agency.displayValue}
                    disabled={isSubmitting}
                    onClear={() => {
                      setValues(initialValues);
                    }}
                    onBlur={e => {
                      if (
                        values[prefix].agency.displayValue &&
                        !values[prefix].agency.selectedId
                      ) {
                        setFieldValue(`${prefix}.agency.displayValue`, '');
                      }
                      handleBlur(e);
                    }}
                    onSelect={(selectedId, options) => {
                      const selectedOption = options.find(
                        option => option.id === selectedId
                      );

                      if (selectedOption) {
                        setValues({
                          [prefix]: {
                            agency: {
                              displayValue: selectedOption.name,
                              selectedId: selectedOption.id,
                            },
                          },
                        });
                      }
                    }}
                    transformOptions={(options: PartnerTypeaheadResponse) =>
                      options.reduce((acc, current) => {
                        return {
                          ...acc,
                          [current.id]: (
                            <TypeaheadResult primaryResult={current.name} />
                          ),
                        };
                      }, {})
                    }
                  />
                  <div className={styles.buttonContainer}>
                    <Button
                      block
                      data-testid="move-user-cancel-button"
                      variant="secondary"
                      onClick={close}
                    >
                      Cancel
                    </Button>
                    <Button
                      block
                      data-testid="move-user-confirm-button"
                      variant="primary"
                      isSubmitting={isSubmitting}
                      onClick={() => {
                        handleSubmit();
                      }}
                      disabled={!values[prefix].agency.selectedId}
                    >
                      Move User
                    </Button>
                  </div>
                  {changePartnerError && (
                    <Alert
                      className={styles.error}
                      message={changePartnerError.message}
                    />
                  )}
                </div>
              )}
            </Formik>
          )}
        </Modal>
      }
    />
  ) : null;
};
